import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  DeleteOutline,
  HistoryOutlined,
  BugReportOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import ConfirmationModal from '@/components/common/confirmation-modal';
import { useGetVmDetailQuery } from '@/state/queries/vm-admin/use-get-vm-detail';
import { useGetVmEventsQuery } from '@/state/queries/vm-admin/use-get-vm-events';
import { useGetVmAgentLogsQuery } from '@/state/queries/vm-admin/use-get-vm-agent-logs';
import { useStartVmMutation } from '@/state/mutations/vm-admin/use-start-vm';
import { useStopVmMutation } from '@/state/mutations/vm-admin/use-stop-vm';
import { useDeleteVmMutation } from '@/state/mutations/vm-admin/use-delete-vm';
import { VmStatus } from '@/enums/vm-status.enum';
import type { VmAdminListItem, VmEvent, VmAgentLog } from '@/types/dtos/vm/response/vm-admin.dto';
import { formatVmFontPackEventMessage, formatVmFontPackTooltip } from '@/helpers/vm-font-pack.helper';

function InfoField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value ?? '--'}</Typography>
    </Box>
  );
}

function EventItem({ event }: { event: VmEvent }) {
  const message = formatVmFontPackEventMessage(event) ?? event.message;
  const isFontPackEvent = event.type === 'FONT_PACK_REFRESHED';

  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, fontFamily: 'monospace' }}>
        {new Date(event.timestamp).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </Typography>
      <Chip
        label={event.type}
        size="small"
        variant="outlined"
        color={isFontPackEvent ? 'success' : 'default'}
        sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
      />
      {message && (
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {message}
        </Typography>
      )}
    </Stack>
  );
}

function AgentLogItem({ log }: { log: VmAgentLog }) {
  return (
    <Stack direction="row" spacing={2} sx={{ py: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, fontFamily: 'monospace' }}>
        {new Date(log.timestamp).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </Typography>
      <Chip
        label={log.level}
        size="small"
        variant="outlined"
        color={log.level === 'error' ? 'error' : log.level === 'info' ? 'info' : 'warning'}
        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', minWidth: 64 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', minWidth: 100 }}>
        {log.category}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, wordBreak: 'break-word' }}>
        {log.message}
      </Typography>
    </Stack>
  );
}

export default function VmDetailPanel({ vm }: { vm: VmAdminListItem }) {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState(0);
  const [confirmAction, setConfirmAction] = useState<'stop' | 'delete' | null>(null);

  const { data: detail, isLoading: detailLoading } = useGetVmDetailQuery(vm.vmId);
  const { data: eventsData, isLoading: eventsLoading } = useGetVmEventsQuery(activeTab === 1 ? vm.vmId : null);
  const { data: agentLogsData, isLoading: agentLogsLoading } = useGetVmAgentLogsQuery(activeTab === 2 ? vm.vmId : null);

  const startMutation = useStartVmMutation();
  const stopMutation = useStopVmMutation();
  const deleteMutation = useDeleteVmMutation();

  const canStart = vm.status === VmStatus.STOPPED;
  const canStop = vm.status === VmStatus.RUNNING || vm.status === VmStatus.CONTAINER_STARTED;
  const canDelete = vm.status !== VmStatus.TERMINATED;
  const isMutating = startMutation.isPending || stopMutation.isPending || deleteMutation.isPending;

  const handleStart = () => startMutation.mutate(vm.vmId);
  const handleConfirmAction = () => {
    if (confirmAction === 'stop') {
      stopMutation.mutate(vm.vmId, { onSettled: () => setConfirmAction(null) });
      return;
    }
    if (confirmAction === 'delete') {
      deleteMutation.mutate(vm.vmId, { onSettled: () => setConfirmAction(null) });
    }
  };

  return (
    <Box sx={{ px: 2, py: 2 }}>
      {/* Actions */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          color="success"
          startIcon={startMutation.isPending ? <CircularProgress size={16} /> : <PlayArrow />}
          disabled={!canStart || isMutating}
          onClick={handleStart}
        >
          {t('common.start') || 'Start'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="warning"
          startIcon={stopMutation.isPending ? <CircularProgress size={16} /> : <Stop />}
          disabled={!canStop || isMutating}
          onClick={() => setConfirmAction('stop')}
        >
          {t('common.stop') || 'Stop'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={deleteMutation.isPending ? <CircularProgress size={16} /> : <DeleteOutline />}
          disabled={!canDelete || isMutating}
          onClick={() => setConfirmAction('delete')}
        >
          {t('common.delete') || 'Delete'}
        </Button>
      </Stack>

      <ConfirmationModal
        open={confirmAction !== null}
        title={
          confirmAction === 'delete'
            ? t('admin.vmAdmin.confirmDeleteTitle', undefined, 'Delete VM?')
            : t('admin.vmAdmin.confirmStopTitle', undefined, 'Stop VM?')
        }
        text={
          confirmAction === 'delete'
            ? t('admin.vmAdmin.confirmDelete', undefined, `Delete VM "${vm.vmName}"? This cannot be undone.`)
            : t(
                'admin.vmAdmin.confirmStop',
                undefined,
                `Stop VM "${vm.vmName}"? Running workloads on this VM will be interrupted.`,
              )
        }
        confirmLabel={
          confirmAction === 'delete' ? t('common.delete', undefined, 'Delete') : t('common.stop', undefined, 'Stop')
        }
        isLoading={confirmAction === 'delete' ? deleteMutation.isPending : stopMutation.isPending}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleConfirmAction}
      />

      {(startMutation.isError || stopMutation.isError || deleteMutation.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(startMutation.error ?? stopMutation.error ?? deleteMutation.error)?.message || 'Action failed'}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2, minHeight: 36 }}>
        <Tab label={t('common.details') || 'Details'} sx={{ minHeight: 36, py: 0 }} />
        <Tab
          label={t('admin.vmAdmin.events') || 'Events'}
          icon={<HistoryOutlined sx={{ fontSize: 16 }} />}
          iconPosition="start"
          sx={{ minHeight: 36, py: 0 }}
        />
        <Tab
          label={t('admin.vmAdmin.agentLogs') || 'Agent Logs'}
          icon={<BugReportOutlined sx={{ fontSize: 16 }} />}
          iconPosition="start"
          sx={{ minHeight: 36, py: 0 }}
        />
      </Tabs>

      {activeTab === 0 && (
        <>
          {detailLoading ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoField
                  label={t('admin.vmAdmin.detail.vmId') || 'VM ID'}
                  value={
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {vm.vmId}
                    </Typography>
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoField label={t('admin.vmAdmin.columns.profile') || 'Profile'} value={vm.profile} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoField label={t('admin.vmAdmin.columns.size') || 'Size'} value={vm.vmSize} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <InfoField
                  label={t('admin.vmAdmin.activationStatus') || 'Activation'}
                  value={
                    vm.activationStatus ? <Chip label={vm.activationStatus} size="small" variant="outlined" /> : '--'
                  }
                />
              </Grid>

              {detail && (
                <>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.commChannel') || 'Comm Channel'}
                      value={detail.communicationChannel}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.wsConnected') || 'WS Connected'}
                      value={
                        detail.websocketConnectedAt ? new Date(detail.websocketConnectedAt).toLocaleString() : '--'
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.wsLastConnected') || 'WS Last Connected'}
                      value={
                        detail.websocketLastConnectedAt
                          ? new Date(detail.websocketLastConnectedAt).toLocaleString()
                          : '--'
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.detail.parentId') || 'Parent ID'}
                      value={detail.parentId ?? '--'}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField label={t('admin.vmAdmin.detail.eventId') || 'Event ID'} value={detail.eventId ?? '--'} />
                  </Grid>
                  {detail.fontPack && (
                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                      <InfoField
                        label={t('admin.vmAdmin.fontPack', undefined, 'Font pack')}
                        value={
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {!detail.fontPack.upToDate && (
                              <Tooltip title={formatVmFontPackTooltip(detail.fontPack)}>
                                <WarningAmberOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
                              </Tooltip>
                            )}
                            <Typography variant="body2">{formatVmFontPackTooltip(detail.fontPack)}</Typography>
                          </Stack>
                        }
                      />
                    </Grid>
                  )}
                </>
              )}

              {Boolean(detail?.metadata?.interactiveUser) && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('admin.vmAdmin.interactiveSession') || 'Interactive Session (dev)'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.interactiveUser') || 'User'}
                      value={String(detail?.metadata?.interactiveUser)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.interactivePassword') || 'Password'}
                      value={
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {String(detail?.metadata?.interactivePassword ?? '')}
                        </Typography>
                      }
                    />
                  </Grid>
                </>
              )}

              {vm.errorMessage && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                    {vm.errorMessage}
                  </Alert>
                </Grid>
              )}

              {vm.costTracking && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('admin.vmAdmin.costTracking') || 'Cost Tracking'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.hourlyRate') || 'Hourly Rate'}
                      value={`$${vm.costTracking.estimatedHourlyCostUSD.toFixed(3)}/hr`}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.runtime') || 'Runtime'}
                      value={`${vm.costTracking.totalRuntimeMinutes.toFixed(0)} min`}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <InfoField
                      label={t('admin.vmAdmin.totalCost') || 'Est. Total Cost'}
                      value={`$${vm.costTracking.estimatedTotalCostUSD.toFixed(2)}`}
                    />
                  </Grid>
                  {vm.budgetLimitUSD != null && (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <InfoField
                        label={t('admin.vmAdmin.budgetLimit') || 'Budget Limit'}
                        value={`$${vm.budgetLimitUSD.toFixed(2)}`}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </>
      )}

      {activeTab === 1 && (
        <Box>
          {eventsLoading ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : eventsData && eventsData.items.length > 0 ? (
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <Stack divider={<Divider flexItem />}>
                {[...eventsData.items].reverse().map((event) => (
                  <EventItem key={`${event.timestamp}-${event.type}`} event={event} />
                ))}
              </Stack>
              {eventsData.total > eventsData.items.length && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('admin.vmAdmin.showingEvents', {
                    shown: eventsData.items.length,
                    total: eventsData.total,
                  }) || `Showing ${eventsData.items.length} of ${eventsData.total} events`}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {t('admin.vmAdmin.noEvents') || 'No events recorded'}
            </Typography>
          )}
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          {agentLogsLoading ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : agentLogsData && agentLogsData.items.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Stack divider={<Divider flexItem />}>
                {[...agentLogsData.items].reverse().map((log, idx) => (
                  <AgentLogItem key={`${log.timestamp}-${idx}`} log={log} />
                ))}
              </Stack>
              {agentLogsData.total > agentLogsData.items.length && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('admin.vmAdmin.showingAgentLogs', {
                    shown: agentLogsData.items.length,
                    total: agentLogsData.total,
                  }) || `Showing ${agentLogsData.items.length} of ${agentLogsData.total} agent logs`}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {t('admin.vmAdmin.noAgentLogs') || 'No agent logs recorded'}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
